import { z } from "zod";

import type { ResourceAvailability } from "@/features/chat/resourceAvailabilitySchemas";
import type {
  MerryChatContextBarResourceContent,
  MerryChatContextBarResourceMetric,
} from "@/shared/ui/MerryChatContextBar";

export type BillingResourceView = "credits" | "plans" | "storage";

export const conversationResourceLimitDataSchema = z
  .object({
    availabilityRevision: z.string().regex(/^\d+$/).nullable(),
    availableQuantity: z.string().regex(/^-?\d+$/),
    refreshesAt: z.string().datetime({ offset: true }).nullable(),
    remainingBasisPoints: z.number().int().min(0).max(10_000).nullable(),
    resource: z.literal("ai_credit"),
    retryable: z.literal(true),
    schema: z.literal("conversation_resource_limit.v1"),
    state: z.literal("exhausted"),
    totalQuantity: z.string().regex(/^\d+$/).nullable(),
  })
  .strict();

export const conversationResourceStateErrorDataSchema = z
  .object({
    resource: z.literal("ai_credit"),
    retryable: z.literal(true),
    schema: z.literal("conversation_resource_state_error.v1"),
    state: z.literal("unknown"),
  })
  .strict();

export type ConversationResourceGate =
  | {
      id: string;
      type: "data-resource-limit";
      data: z.infer<typeof conversationResourceLimitDataSchema>;
    }
  | {
      id: string;
      type: "data-resource-state-error";
      data: z.infer<typeof conversationResourceStateErrorDataSchema>;
    };

export function projectConversationResourceGate(
  value: unknown,
): ConversationResourceGate | null {
  if (!isRecord(value) || typeof value.id !== "string") return null;
  if (value.type === "data-resource-limit") {
    const parsed = conversationResourceLimitDataSchema.safeParse(value.data);
    return parsed.success
      ? { data: parsed.data, id: value.id, type: value.type }
      : null;
  }
  if (value.type === "data-resource-state-error") {
    const parsed = conversationResourceStateErrorDataSchema.safeParse(value.data);
    return parsed.success
      ? { data: parsed.data, id: value.id, type: value.type }
      : null;
  }
  return null;
}

export interface WorkspaceResourceCapabilities {
  canSendAI: boolean;
  canUploadNewFiles: boolean;
}

export function selectWorkspaceResourceCapabilities(
  availability: ResourceAvailability | undefined,
): WorkspaceResourceCapabilities {
  return {
    canSendAI: availability?.aiCredit.state !== "exhausted",
    canUploadNewFiles: availability?.cloudStorage.state !== "exhausted",
  };
}

export function selectResourceContextBar(
  availability: ResourceAvailability | undefined,
): MerryChatContextBarResourceContent | null {
  if (!availability) return null;
  const metrics: MerryChatContextBarResourceMetric[] = [];
  if (
    availability.aiCredit.state === "low" ||
    availability.aiCredit.state === "exhausted"
  ) {
    metrics.push({
      current: formatCredit(availability.aiCredit.availableQuantity),
      label: "积分剩余",
      percent: formatPercent(availability.aiCredit.remainingBasisPoints),
      state: availability.aiCredit.state,
      total: formatCredit(availability.aiCredit.totalQuantity),
      unit: "积分",
    });
  }
  if (
    availability.cloudStorage.state === "low" ||
    availability.cloudStorage.state === "exhausted"
  ) {
    metrics.push({
      current: formatBytes(availability.cloudStorage.availableQuantity),
      label: "存储剩余",
      percent: formatPercent(availability.cloudStorage.remainingBasisPoints),
      state: availability.cloudStorage.state,
      total: formatBytes(availability.cloudStorage.totalQuantity),
      unit: "G",
    });
  }
  if (metrics.length === 0) return null;
  const tuple = metrics.length === 1
    ? ([metrics[0]!] as const)
    : ([metrics[0]!, metrics[1]!] as const);
  return {
    action:
      tuple.length === 1
        ? {
            mode: "direct" as const,
            target: tuple[0].label === "存储剩余" ? "storage" : "credits",
          }
        : { mode: "menu" as const },
    metrics: tuple,
    type: "resource",
  };
}

export function resourceAvailabilityDismissKey(
  availability: ResourceAvailability,
) {
  const resourceSnapshots = [
    availability.aiCredit.state === "low" ||
    availability.aiCredit.state === "exhausted"
      ? `ai_credit@${availability.aiCredit.revision ?? "unknown"}`
      : "",
    availability.cloudStorage.state === "low" ||
    availability.cloudStorage.state === "exhausted"
      ? `cloud_storage@${availability.cloudStorage.revision ?? "unknown"}`
      : "",
  ].filter(Boolean);
  return [
    availability.organizationId,
    resourceSnapshots.join("+"),
  ].join(":");
}

export function formatResourceRefreshTime(value: string | null | undefined) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("zh-CN", {
    day: "numeric",
    hour: "2-digit",
    hour12: false,
    minute: "2-digit",
    month: "long",
    timeZoneName: "short",
  }).format(date);
}

function formatPercent(value: number | null) {
  if (value === null) return "--";
  const percent = value / 100;
  return `${new Intl.NumberFormat("zh-CN", { maximumFractionDigits: 1 }).format(percent)}%`;
}

function formatCredit(value: string | null) {
  if (value === null) return "--";
  try {
    return new Intl.NumberFormat("zh-CN").format(BigInt(value));
  } catch {
    return "--";
  }
}

function formatBytes(value: string | null) {
  if (value === null) return "--";
  try {
    const bytes = BigInt(value);
    const tenths = (bytes * 10n) / (1024n ** 3n);
    const integer = tenths / 10n;
    const fraction = tenths % 10n;
    return fraction === 0n ? integer.toString() : `${integer}.${fraction}`;
  } catch {
    return "--";
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
